import { Link } from "@inertiajs/react"
import { useState } from "react"

import PlatformIcon from "@/components/icons/platform-icon"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useTimeZone } from "@/hooks/use-time-zone"
import { inHoursWallClock, tomorrowMorningWallClock } from "@/lib/scheduling"
import {
  type StorySyndicationsAttributes,
  syndicationEntryKey,
} from "@/pages/stories/story-form-utils"
import { connectedAccountsPath, settingsProfilePath } from "@/routes"
import type {
  Platform,
  PlatformCredential,
  PlatformCredentials,
  YoutubeCategory,
} from "@/types"

interface Props {
  availablePlatforms: Platform[]
  syndicationsAttributes: StorySyndicationsAttributes
  publishAt: string
  onSyndicationsAttributesChange: (next: StorySyndicationsAttributes) => void
  onPublishAtChange: (value: string) => void
  platformsError?: string | null
  publishAtError?: string | null
  lockedKeys?: string[]
  lockedStatusLabels?: Record<string, string>
  platformCredentials?: PlatformCredentials
  youtubeCategories: YoutubeCategory[]
  defaultYoutubeCategoryId: number
  title: string
  tags: string[]
  onTitleChange: (value: string) => void
  onTagsChange: (value: string[]) => void
}

// One selectable row per connected account. A platform with two connected
// accounts (e.g. two X accounts) produces two rows, each becoming its own
// delivery. Dev-only platforms that need no credential get a single row.
interface AccountRow {
  key: string
  platform: Platform
  credential: PlatformCredential | null
}

export default function StoryPlatformPicker({
  availablePlatforms,
  syndicationsAttributes,
  publishAt,
  onSyndicationsAttributesChange,
  onPublishAtChange,
  platformsError,
  publishAtError,
  lockedKeys = [],
  lockedStatusLabels = {},
  platformCredentials = [],
  youtubeCategories,
  defaultYoutubeCategoryId,
  title,
  tags,
  onTitleChange,
  onTagsChange,
}: Props) {
  const [tagsInput, setTagsInput] = useState(() => tags.join(", "))
  const timeZone = useTimeZone()
  const timeZoneAbbreviation =
    new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value ?? timeZone

  const rows: AccountRow[] = availablePlatforms.flatMap<AccountRow>(
    (platform) => {
      if (!platform.requires_credential) {
        return [
          {
            key: syndicationEntryKey(platform.name, null),
            platform,
            credential: null,
          },
        ]
      }

      return platformCredentials
        .filter((credential) => credential.platform_name === platform.name)
        .map((credential) => ({
          key: syndicationEntryKey(platform.name, credential.id),
          platform,
          credential,
        }))
    },
  )

  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
          Publish to
        </p>
        <p className="text-muted-foreground text-sm">
          Connect a social account on{" "}
          <Link
            href={connectedAccountsPath()}
            className="underline underline-offset-4"
          >
            Connected Accounts
          </Link>{" "}
          to syndicate this story.
        </p>
      </div>
    )
  }

  const attrEntries = Object.entries(syndicationsAttributes).map(
    ([key, attr]) => ({ key: Number(key), attr }),
  )

  function findAttrEntry(row: AccountRow) {
    return attrEntries.find(({ attr }) =>
      row.credential
        ? attr.platform_credential_id === row.credential.id
        : attr.platform_id === row.platform.id &&
          attr.platform_credential_id == null,
    )
  }

  function isSelected(row: AccountRow) {
    if (lockedKeys.includes(row.key)) return true
    const entry = findAttrEntry(row)
    return entry !== undefined && !entry.attr._destroy
  }

  function nextKey() {
    const usedKeys = attrEntries.map(({ key }) => key)
    return usedKeys.length === 0 ? 0 : Math.max(...usedKeys) + 1
  }

  function defaultsForRow(row: AccountRow) {
    if (row.platform.requires_category) {
      return { youtube_category_id: defaultYoutubeCategoryId }
    }
    if (row.platform.requires_page_selection) {
      return { facebook_page_id: row.credential?.facebook_pages[0]?.id ?? null }
    }
    if (row.platform.requires_board_selection) {
      return {
        pinterest_board_id: row.credential?.pinterest_boards[0]?.id ?? null,
      }
    }
    return {}
  }

  function toggleRow(row: AccountRow, checked: boolean) {
    if (lockedKeys.includes(row.key)) return

    const entry = findAttrEntry(row)
    const next = { ...syndicationsAttributes }

    if (checked) {
      if (!entry) {
        next[nextKey()] = {
          type: row.platform.syndication_class_name,
          platform_id: row.platform.id,
          platform_credential_id: row.credential?.id ?? null,
          ...defaultsForRow(row),
        }
      } else if (entry.attr._destroy) {
        next[entry.key] = { ...entry.attr, _destroy: false }
      } else {
        return
      }
    } else {
      if (!entry) return
      if (entry.attr.id) {
        next[entry.key] = { ...entry.attr, _destroy: true }
      } else {
        delete next[entry.key]
      }
    }

    onSyndicationsAttributesChange(next)
  }

  function updateRow(
    row: AccountRow,
    changes: Partial<StorySyndicationsAttributes[number]>,
  ) {
    const entry = findAttrEntry(row)
    if (!entry) return

    onSyndicationsAttributesChange({
      ...syndicationsAttributes,
      [entry.key]: { ...entry.attr, ...changes },
    })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor="story-publish-at">Schedule for later</Label>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPublishAtChange(inHoursWallClock(1, timeZone))}
          >
            In 1 hour
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPublishAtChange(inHoursWallClock(3, timeZone))}
          >
            In 3 hours
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              onPublishAtChange(tomorrowMorningWallClock(timeZone))
            }
          >
            Tomorrow 9am
          </Button>
        </div>
        <Input
          id="story-publish-at"
          type="datetime-local"
          value={publishAt}
          onChange={(event) => onPublishAtChange(event.target.value)}
        />
        <p className="text-muted-foreground text-xs">
          Times shown in {timeZone} ({timeZoneAbbreviation}).{" "}
          <Link
            href={settingsProfilePath()}
            className="underline underline-offset-4"
          >
            Change in Settings
          </Link>
        </p>
        {publishAtError && (
          <p className="text-destructive text-sm">{publishAtError}</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
          Publish to
        </p>
        {platformsError && (
          <p className="text-destructive text-sm">{platformsError}</p>
        )}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {rows.map((row) => {
            const { platform, credential } = row
            const id = `syndication-${row.key}`
            const locked = lockedKeys.includes(row.key)
            const checked = isSelected(row)
            const label = credential?.display_label ?? platform.label
            const statusLabel = lockedStatusLabels[row.key]
            const entry = findAttrEntry(row)
            const facebookPages = credential?.facebook_pages ?? []
            const pinterestBoards = credential?.pinterest_boards ?? []
            const showYoutubeCategoryPicker =
              platform.requires_category && youtubeCategories.length > 0
            const showFacebookPagePicker =
              platform.requires_page_selection && facebookPages.length > 0
            const showPinterestBoardPicker =
              platform.requires_board_selection && pinterestBoards.length > 0
            const hasExtraFields =
              showYoutubeCategoryPicker ||
              showFacebookPagePicker ||
              showPinterestBoardPicker ||
              platform.supports_title ||
              platform.supports_tags
            const checkboxRow = (
              <div className="flex min-w-0 items-center gap-3">
                <Checkbox
                  id={id}
                  checked={checked}
                  disabled={locked}
                  onCheckedChange={(value) => toggleRow(row, value === true)}
                />
                <div className="bg-muted text-foreground flex size-8 shrink-0 items-center justify-center rounded-full">
                  <PlatformIcon platform={platform.name} className="size-4" />
                </div>
                {credential?.avatar_url && (
                  <Avatar className="size-6 shrink-0">
                    <AvatarImage src={credential.avatar_url} alt="" />
                    <AvatarFallback />
                  </Avatar>
                )}
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium">{label}</span>
                  {locked && statusLabel && (
                    <span className="text-muted-foreground truncate text-xs">
                      {statusLabel}
                    </span>
                  )}
                </div>
              </div>
            )
            const cardClassName =
              "flex items-start gap-6 rounded-lg border p-3 font-normal data-[disabled=true]:opacity-60 data-[disabled=false]:cursor-pointer data-[disabled=true]:cursor-not-allowed"
            if (!hasExtraFields) {
              return (
                <Label
                  key={row.key}
                  htmlFor={id}
                  className={cardClassName}
                  data-disabled={locked}
                >
                  {checkboxRow}
                </Label>
              )
            }
            return (
              <div
                key={row.key}
                className="flex flex-col gap-3 rounded-lg border p-3 data-[disabled=true]:opacity-60 md:flex-row md:items-start md:gap-6"
                data-disabled={locked}
              >
                <Label
                  htmlFor={id}
                  className="font-normal data-[disabled=false]:cursor-pointer data-[disabled=true]:cursor-not-allowed"
                  data-disabled={locked}
                >
                  {checkboxRow}
                </Label>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  {showYoutubeCategoryPicker && (
                    <div className="flex flex-col gap-1">
                      <Label
                        htmlFor={`${id}-category`}
                        className="text-muted-foreground text-xs"
                      >
                        Category
                      </Label>
                      <Select
                        value={(
                          entry?.attr.youtube_category_id ??
                          defaultYoutubeCategoryId
                        ).toString()}
                        onValueChange={(value) =>
                          updateRow(row, {
                            youtube_category_id: Number(value),
                          })
                        }
                        disabled={!checked || locked}
                      >
                        <SelectTrigger
                          id={`${id}-category`}
                          aria-label="YouTube category"
                          className="h-9 w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {youtubeCategories.map((category) => (
                            <SelectItem
                              key={category.id}
                              value={category.id.toString()}
                            >
                              {category.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {showFacebookPagePicker && (
                    <div className="flex flex-col gap-1">
                      <Label
                        htmlFor={`${id}-page`}
                        className="text-muted-foreground text-xs"
                      >
                        Page
                      </Label>
                      <Select
                        value={(
                          entry?.attr.facebook_page_id ?? facebookPages[0].id
                        ).toString()}
                        onValueChange={(value) =>
                          updateRow(row, { facebook_page_id: Number(value) })
                        }
                        disabled={!checked || locked}
                      >
                        <SelectTrigger
                          id={`${id}-page`}
                          aria-label="Facebook page"
                          className="h-9 w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {facebookPages.map((page) => (
                            <SelectItem
                              key={page.id}
                              value={page.id.toString()}
                            >
                              {page.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {showPinterestBoardPicker && (
                    <div className="flex flex-col gap-1">
                      <Label
                        htmlFor={`${id}-board`}
                        className="text-muted-foreground text-xs"
                      >
                        Board
                      </Label>
                      <Select
                        value={(
                          entry?.attr.pinterest_board_id ??
                          pinterestBoards[0].id
                        ).toString()}
                        onValueChange={(value) =>
                          updateRow(row, { pinterest_board_id: Number(value) })
                        }
                        disabled={!checked || locked}
                      >
                        <SelectTrigger
                          id={`${id}-board`}
                          aria-label="Pinterest board"
                          className="h-9 w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {pinterestBoards.map((board) => (
                            <SelectItem
                              key={board.id}
                              value={board.id.toString()}
                            >
                              {board.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {platform.supports_title && (
                    <div className="flex flex-col gap-1">
                      <Label
                        htmlFor={`${id}-title`}
                        className="text-muted-foreground text-xs"
                      >
                        Title
                      </Label>
                      <Input
                        id={`${id}-title`}
                        value={title}
                        onChange={(e) => onTitleChange(e.target.value)}
                        disabled={!checked || locked}
                        placeholder="Optional post title"
                        className="h-9 w-full"
                      />
                    </div>
                  )}
                  {platform.supports_tags && (
                    <div className="flex flex-col gap-1">
                      <Label
                        htmlFor={`${id}-tags`}
                        className="text-muted-foreground text-xs"
                      >
                        Tags
                      </Label>
                      <Input
                        id={`${id}-tags`}
                        value={tagsInput}
                        onChange={(e) => {
                          setTagsInput(e.target.value)
                          onTagsChange(
                            e.target.value
                              .split(",")
                              .map((tag) => tag.trim())
                              .filter(Boolean),
                          )
                        }}
                        disabled={!checked || locked}
                        placeholder="ruby, rails"
                        className="h-9 w-full"
                      />
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
